import React, { useState } from 'react';
import { Pencil, Receipt, FileText, CheckCircle, Prohibit, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { formatCurrency, formatDate } from '../../lib/utils';

const SOURCE_BADGES = {
  '835': 'bg-amber-50 text-amber-700 border-amber-100',
  '277CA': 'bg-red-50 text-red-700 border-red-100',
  'TA1': 'bg-purple-50 text-purple-700 border-purple-100',
};

const TRIAGE_BADGES = {
  resolved: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  voided: 'bg-slate-100 text-slate-500 border-slate-200',
  open: 'bg-orange-50 text-orange-700 border-orange-100',
};

const TriageBadge = ({ status }) => {
  const key = status || 'open';
  const cls = TRIAGE_BADGES[key] || TRIAGE_BADGES.open;
  const label = key === 'open' ? 'Needs action' : key.charAt(0).toUpperCase() + key.slice(1);
  return (
    <span className={`text-[10px] font-medium border rounded px-1.5 py-0.5 ${cls}`}>{label}</span>
  );
};

const DenialRow = ({ row, onAdjudication, onTriage }) => {
  const triageKey = row.triage_status === 'resolved' || row.triage_status === 'voided'
    ? row.triage_status : 'open';
  return (
    <TableRow data-testid={`denial-row-${row.id}`}>
      <TableCell>
        <div>
          <span className="font-mono text-xs text-slate-600">{row.claim_number}</span>
          <p className="text-sm font-medium text-slate-900 mt-0.5">{row.patient_name}</p>
        </div>
      </TableCell>
      <TableCell>
        <span className={`text-[10px] font-medium border rounded px-1.5 py-0.5 ${SOURCE_BADGES[row.denial_source] || 'bg-slate-50 text-slate-600 border-slate-100'}`}>
          {row.denial_source}
        </span>
        <div className="flex items-baseline gap-1 mt-1">
          <span className="font-mono font-semibold text-sm text-slate-900">{row.reason_code}</span>
        </div>
        <p className="text-xs text-slate-500 max-w-xs truncate" title={row.reason_label}>
          {row.reason_label}
        </p>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <div>
          <p className="text-sm">{formatDate(row.service_date)}</p>
          <p className="text-xs text-slate-500 font-mono">{row.service_code}</p>
        </div>
      </TableCell>
      <TableCell>
        <div className="font-mono text-sm">
          {formatCurrency(row.charge_amount)}
        </div>
        {row.paid_amount > 0 && (
          <div className="text-xs text-emerald-700 font-mono">
            paid {formatCurrency(row.paid_amount)}
          </div>
        )}
        {row.reason_amount > 0 && (
          <div className="text-xs text-red-600 font-mono">
            -{formatCurrency(row.reason_amount)}
          </div>
        )}
      </TableCell>
      <TableCell>
        <TriageBadge status={triageKey} />
        {row.triaged_by_name && triageKey !== 'open' && (
          <p className="text-[10px] text-slate-400 mt-0.5">by {row.triaged_by_name}</p>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onAdjudication(row)}
            className="h-8 px-2"
            title="View full adjudication"
            data-testid={`view-adj-${row.id}`}
          >
            <Receipt size={14} />
          </Button>
          {triageKey === 'open' && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onTriage(row, 'fix')}
                className="h-8 px-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                title="Reopen as Draft to fix and resubmit"
                data-testid={`fix-resubmit-${row.id}`}
              >
                <Pencil size={12} className="mr-1" />
                Fix &amp; resubmit
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onTriage(row, 'resolve')}
                className="h-8 px-2 text-emerald-700 hover:bg-emerald-50"
                title="Mark as resolved (handled offline)"
                data-testid={`resolve-${row.id}`}
              >
                <CheckCircle size={12} />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onTriage(row, 'void')}
                className="h-8 px-2 text-red-600 hover:bg-red-50"
                title="Void claim"
                data-testid={`void-${row.id}`}
              >
                <Prohibit size={12} />
              </Button>
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

const TableLoadingState = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
  </div>
);

const TableEmptyState = () => (
  <div className="text-center py-12">
    <FileText size={48} className="mx-auto mb-4 text-slate-300" />
    <p className="text-slate-500" data-testid="denials-empty">No denials match the current filters</p>
    <p className="text-xs text-slate-400 mt-1">Try widening your date range or clearing CARC/277 filters.</p>
  </div>
);

const DenialsTable = ({
  rows, total, totalPages, page, setPage, loading, onAdjudication, onTriage,
}) => (
  <Card className="border-slate-200">
    <CardContent className="p-0">
      {loading && <TableLoadingState />}
      {!loading && rows.length === 0 && <TableEmptyState />}
      {!loading && rows.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Claim / Patient</TableHead>
                  <TableHead>Source &amp; Reason</TableHead>
                  <TableHead className="hidden md:table-cell">Service</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Triage</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <DenialRow
                    key={row.id}
                    row={row}
                    onAdjudication={onAdjudication}
                    onTriage={onTriage}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t border-slate-100">
              <p className="text-xs text-slate-500">
                Showing page {page} of {totalPages} · {total} total
              </p>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  data-testid="denials-prev-page"
                >
                  <CaretLeft size={14} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  data-testid="denials-next-page"
                >
                  <CaretRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </CardContent>
  </Card>
);

const TONE_BY_ACTION = {
  void: 'bg-red-600 hover:bg-red-700',
  resolve: 'bg-emerald-700 hover:bg-emerald-800',
  fix: 'bg-indigo-900 hover:bg-indigo-800',
};

export const TriageConfirmDialog = ({ pending, onClose, onConfirm }) => {
  const [notes, setNotes] = useState('');
  const [working, setWorking] = useState(false);
  if (!pending) return null;
  const verb = { fix: 'Reopen as Draft', resolve: 'Mark resolved', void: 'Void' }[pending.action];
  const tone = TONE_BY_ACTION[pending.action] || TONE_BY_ACTION.fix;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="triage-confirm-dialog">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5">
        <h3 className="font-semibold text-lg" style={{ fontFamily: 'Manrope' }}>{verb}</h3>
        <p className="text-sm text-slate-500 mt-1 mb-3">
          <strong>{pending.row.claim_number}</strong> · {pending.row.patient_name}
        </p>
        <input
          type="text"
          placeholder="Optional notes (e.g., 'Wrong NPI, will correct')"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full border border-slate-200 rounded px-3 py-2 text-sm mb-3"
          data-testid="triage-confirm-notes"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={working}>Cancel</Button>
          <Button
            className={`${tone} text-white`}
            disabled={working}
            onClick={async () => {
              setWorking(true);
              const ok = await onConfirm(pending.row.id, pending.action, notes);
              setWorking(false);
              if (ok) onClose();
            }}
            data-testid="triage-confirm-action"
          >
            {working ? 'Working…' : verb}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DenialsTable;
